import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CardContent,
  Typography,
  IconButton,
  CardMedia,
  Button,
  Card,
} from "@mui/material";
import {
  TwoWheeler,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { getUsedBikesUnauthorized } from "../../State/UsedBike/Action";
import AppPagination from "../Tools/AppPagination";
import bluewasherImage from "./images/bluewasher_landing_image.png";
import { 
  HiSparkles, 
  HiBolt, 
  HiShieldCheck, 
  HiHeart, 
  HiUser, 
  HiEye, 
  HiMapPin,
  HiCurrencyDollar,
  HiStar
} from "react-icons/hi2";

const LandingPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const usedBikes = useSelector(
    (store) => store.usedBikesResponse.usedBikesPaged.usedBikes
  );
  const totalPages = useSelector(
    (store) => store.usedBikesResponse.usedBikesPaged.totalPages
  );

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getUsedBikesUnauthorized(page));
  }, [page]);

  const features = [
    {
      icon: <HiSparkles className="w-8 h-8 text-accent-500" />,
      title: "Premium Quality",
      description: "Carefully curated bikes with verified condition"
    },
    {
      icon: <HiBolt className="w-8 h-8 text-primary-500" />,
      title: "Fast Delivery",
      description: "Quick and secure bike delivery to your doorstep"
    },
    {
      icon: <HiShieldCheck className="w-8 h-8 text-green-500" />,
      title: "Trusted Platform",
      description: "Secure transactions with buyer protection"
    }
  ];

  return (
    <div className="w-full min-h-screen gradient-bg overflow-x-hidden">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-soft mb-6">
              <HiSparkles className="w-5 h-5 text-accent-500" />
              <span className="text-sm font-semibold text-gray-700">Find Your Perfect Ride</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Discover Your
            <span className="text-gradient block">Dream Bike</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed"
          >
            From vintage classics to modern marvels, find the perfect bike that matches your style and budget. 
            Quality guaranteed, delivery assured.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/used-bike-list-router")}
                className="btn-primary text-lg px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 w-full max-w-xs"
                startIcon={<TwoWheeler />}
              >
                Used Bikes
              </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-6"
          >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/new-bike-list-router")}
                className="bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 text-white text-lg px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 w-full max-w-xs"
                startIcon={<HiSparkles className="w-5 h-5" />}
              >
                New Bikes
              </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-6"
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/login")}
              className="bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white text-lg px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-700 hover:border-gray-600 w-full max-w-xs"
              startIcon={<HiUser className="w-5 h-5" />}
            >
              Login
            </Button>
          </motion.div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary-200/30 rounded-full blur-xl animate-bounce-gentle"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-accent-200/30 rounded-full blur-xl animate-bounce-gentle" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-green-200/30 rounded-full blur-xl animate-bounce-gentle" style={{ animationDelay: '2s' }}></div>
      </motion.section>

      {/* Features Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="py-16 px-4 sm:px-6 lg:px-8"
      >
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Blue Bike?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Experience the difference with our premium service and quality assurance</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                className="card card-hover p-8 text-center"
              >
                <div className="flex justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Featured Bikes Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white"
      >
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center mb-4">
              <HiStar className="w-8 h-8 text-accent-500 mr-3" />
              <h2 className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                Featured Bikes
              </h2>
            </div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Handpicked bikes that offer the best value, quality, and performance
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {usedBikes?.length > 0 ? (
              usedBikes.slice(0, 6).map((bike, index) => (
                <motion.div
                  key={bike.bikeId}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="group"
                >
                  <Card 
                    className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border-0 cursor-pointer"
                    sx={{ 
                      borderRadius: "24px",
                      "&:hover": {
                        boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
                      }
                    }}
                    onClick={() => navigate(`/used-bike-detail?bikeId=${bike.bikeId}`)}
                  >
                    <div className="relative overflow-hidden">
                      <CardMedia
                        component="img"
                        height="240"
                        image={bike.imageUrl}
                        alt={`${bike.brand} ${bike.model}`}
                        className="transition-transform duration-300 group-hover:scale-110"
                        sx={{
                          objectFit: "cover",
                        }}
                      />
                      
                      {/* Featured Badge */}
                      <div className="absolute top-4 right-4">
                        <div className="bg-gradient-to-r from-accent-500 to-accent-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                          Featured
                        </div>
                      </div>
                      
                      {/* Heart Icon */}
                      <div className="absolute top-4 left-4">
                        <IconButton
                          size="small"
                          className="bg-white/90 backdrop-blur-sm hover:bg-white shadow-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/login");
                          }}
                        >
                          <HiHeart className="w-5 h-5 text-red-500" />
                        </IconButton>
                      </div>
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    
                    <CardContent className="p-6">
                      {/* Bike Title */}
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-primary-100 rounded-lg">
                          <TwoWheeler className="text-primary-600" sx={{ fontSize: "1.2rem" }} />
                        </div>
                        <Typography 
                          variant="h6" 
                          className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors"
                          sx={{ fontSize: "1.1rem" }}
                        >
                          {bike.brand} {bike.model}
                        </Typography>
                      </div>
                      
                      {/* Price */}
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-1.5 bg-green-100 rounded-lg">
                          <HiCurrencyDollar className="w-4 h-4 text-green-600" />
                        </div>
                        <Typography 
                          variant="h5" 
                          className="font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent"
                          sx={{ fontSize: "1.3rem" }}
                        >
                          ₹{bike.price.toLocaleString()}
                        </Typography>
                      </div>
                      
                      {/* Location */}
                      <div className="flex items-center gap-2 mb-6">
                        <div className="p-1.5 bg-gray-100 rounded-lg">
                          <HiMapPin className="w-4 h-4 text-gray-600" />
                        </div>
                        <Typography 
                          variant="body2" 
                          className="text-gray-600 font-medium"
                          sx={{ fontSize: "0.9rem" }}
                        >
                          {bike.sellerCity}
                        </Typography>
                      </div>
                      
                      {/* Action Button */}
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/used-bike-detail?bikeId=${bike.bikeId}`);
                        }}
                        className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl py-3 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                        startIcon={<HiEye className="w-5 h-5" />}
                        sx={{
                          borderRadius: "12px",
                          textTransform: "none",
                          fontSize: "1rem",
                          fontWeight: 600,
                          py: 1.5
                        }}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="col-span-full text-center py-16"
              >
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <TwoWheeler className="w-12 h-12 text-gray-400" />
                </div>
                <Typography 
                  variant="h5" 
                  className="text-gray-500 mb-3 font-semibold"
                  sx={{ fontSize: "1.5rem" }}
                >
                  No bikes available right now
                </Typography>
                <Typography 
                  variant="body1" 
                  className="text-gray-400"
                  sx={{ fontSize: "1rem" }}
                >
                  Please check back soon for amazing deals!
                </Typography>
              </motion.div>
            )}
          </div>

          {usedBikes?.length > 6 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-center mt-12"
            >
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/used-bike-list-router")}
                className="border-2 border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                startIcon={<HiEye className="w-5 h-5" />}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  px: 4,
                  py: 1.5
                }}
              >
                View All Bikes
              </Button>
            </motion.div>
          )}
        </div>
      </motion.section>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center py-8">
          <AppPagination totalPages={totalPages} setPage={setPage} />
        </div>
      )}

      {/* Partners Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="py-16 px-4 sm:px-6 lg:px-8"
      >
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Partners</h2>
          <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
            Trusted by leading brands and service providers in the automotive industry
          </p>
          
          <div className="flex justify-center">
            <motion.a
              whileHover={{ scale: 1.05 }}
              href="https://www.bluewasher.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="card p-8 max-w-md mx-auto">
                <img
                  src={bluewasherImage}
                  alt="Blue Washer"
                  className="w-full h-auto rounded-lg shadow-soft"
                />
                <div className="mt-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Blue Washer</h3>
                  <p className="text-gray-600">Professional car and bike washing services</p>
                </div>
              </div>
            </motion.a>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default LandingPage;
